import {PredicateOf} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {ClientRuleAction} from '../../../ruleActionModel';
import {
  addActionReducerHelper,
  deleteActionReducerHelper,
  updateActionReducerHelper,
} from '../../state/ruleEditorReducer';
import {RuleState} from '../../state/ruleEditorState';

/**
 * Type representing a state with multiple rule actions.
 * This is useful if the state need to create multiples rules (for SLA or CSAT).
 * A subset here is representing a type of rule.
 *
 * Example:
 * {
 *   name: 'blabla',
 *   ...
 *
 *   actions: {
 *     subset1: [<actions>],
 *     subset2: [<actions>],
 *     ...
 *   }
 * }
 */
type StateWithActionsSubsets<TSubset extends string> = {
  actions: Record<TSubset, RuleState['actions']>;
};

export function addActionInSubsetReducer<
  TSubset extends string,
  TState extends StateWithActionsSubsets<TSubset>,
>(state: TState, action: ClientRuleAction, subset: TSubset): TState {
  return {
    ...state,
    actions: {
      ...state.actions,
      [subset]: addActionReducerHelper(state.actions[subset], action),
    },
  };
}

export function updateActionInSubsetReducer<
  TSubset extends string,
  TState extends StateWithActionsSubsets<TSubset>,
>(
  state: TState,
  predicate: PredicateOf<ClientRuleAction>,
  update: (action: ClientRuleAction) => ClientRuleAction,
  subset: TSubset,
): TState {
  return {
    ...state,
    actions: {
      ...state.actions,
      [subset]: updateActionReducerHelper(state.actions[subset], predicate, update),
    },
  };
}

export function deleteActionInSubsetReducer<
  TSubset extends string,
  TState extends StateWithActionsSubsets<TSubset>,
>(state: TState, predicate: PredicateOf<ClientRuleAction>, subset: TSubset): TState {
  const {actions: newActions} = deleteActionReducerHelper(state.actions[subset], predicate, undefined);
  return {
    ...state,
    actions: {
      ...state.actions,
      [subset]: newActions,
    },
  };
}
